import {
    useEffect,
    useState,
} from "react";

import {
    Alert,
    Box,
    Button,
    Divider,
    LinearProgress,
    Paper,
    Typography,
} from "@mui/material";

import DescriptionOutlinedIcon
    from "@mui/icons-material/DescriptionOutlined";

import HomeOutlinedIcon
    from "@mui/icons-material/HomeOutlined";

import {
    Link,
} from "react-router-dom";

import {
    PRIMARY_COLOR,
} from "../util/constants";

import {
    handleResponse,
} from "../util/integration";

import {
    fetchOperatorImprintAPI,
} from "../api/imprint_api";

const ROUTE = Object.freeze({
    home: "/",
});

const LABEL = Object.freeze({
    title: "Impressum",

    subtitle:
        "Betreiber-Impressum",

    backToHome:
        "Home",
});

const MESSAGE = Object.freeze({
    noImprint:
        "Es wurde noch kein Impressum hinterlegt.",

    loadError:
        "Das Impressum konnte nicht geladen werden.",
});

const CONFIG = Object.freeze({
    topSpacing: "50px",

    contentPadding: {
        xs: 3,
        sm: 4,
        md: 5,
    },

    bottomSpacing: {
        xs: 4,
        md: 6,
    },
});

const STYLE = Object.freeze({
    root: {
        width: "100%",
        boxSizing: "border-box",
        marginTop: "50px",
        overflow: "hidden",
    },

    content: {
        width: "100%",
        boxSizing: "border-box",
    },

    heading: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 3,
    },

    headingTitleGroup: {
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
        minWidth: 0,
    },

    headingText: {
        minWidth: 0,
    },

    subtitle: {
        mt: 0.75,
        mb: 0,
        color: "text.secondary",
    },

    homeButton: {
        flexShrink: 0,
        whiteSpace: "nowrap",
    },

    divider: {
        my: {
            xs: 3,
            md: 4,
        },
    },

    imprintText: {
        color: "text.primary",

        fontFamily:
            "Arial, Helvetica, sans-serif",

        fontSize: {
            xs: "0.95rem",
            sm: "1rem",
        },

        lineHeight: 1.75,

        /*
         * Erhält die aus den Dollarzeichen
         * erzeugten Zeilenumbrüche.
         */
        whiteSpace: "pre-wrap",

        overflowWrap: "anywhere",
        wordBreak: "break-word",
    },

    emptyText: {
        color: "text.secondary",
        fontStyle: "italic",
    },
});

function createColorStyles(
    primaryColor,
) {
    return Object.freeze({
        titleIcon: {
            flexShrink: 0,
            mt: 0.25,
            fontSize: 38,
            color: primaryColor,
        },

        title: {
            color: primaryColor,
            fontWeight: 500,
            lineHeight: 1.2,
        },

        homeButton: {
            ...STYLE.homeButton,

            color: primaryColor,
            borderColor: primaryColor,

            "&:hover": {
                borderColor: primaryColor,

                backgroundColor:
                    "rgba(12, 5, 109, 0.06)",
            },
        },

        progress: {
            backgroundColor:
                "rgba(0, 0, 0, 0.06)",

            "& .MuiLinearProgress-bar": {
                backgroundColor:
                    primaryColor,
            },
        },
    });
}

function formatImprintText(
    imprint,
) {
    if (
        typeof imprint !== "string"
    ) {
        return "";
    }

    /*
     * Das Backend verwendet "$" als
     * Platzhalter für Zeilenumbrüche.
     */
    return imprint
        .replaceAll(
            "$",
            "\n",
        )
        .trim();
}

function normalizeImprint(
    response,
) {
    /*
     * Der aktuelle Endpunkt liefert
     * direkt einen String.
     */
    if (
        typeof response === "string"
    ) {
        return formatImprintText(
            response,
        );
    }

    /*
     * Fallback für eine mögliche spätere
     * JSON-Antwort:
     *
     * {
     *     "imprint": "..."
     * }
     */
    if (
        response !== null
        && typeof response === "object"
        && typeof response.imprint
            === "string"
    ) {
        return formatImprintText(
            response.imprint,
        );
    }

    return "";
}

export default function Imprint({
    appAPI,
}) {
    const [
        imprint,
        setImprint,
    ] = useState("");

    const [
        isLoading,
        setIsLoading,
    ] = useState(true);

    const [
        hasError,
        setHasError,
    ] = useState(false);

    const showMessage =
        appAPI?.showMessage;

    const colorStyle =
        createColorStyles(
            PRIMARY_COLOR,
        );

    useEffect(() => {
        let requestIsActive =
            true;

        setImprint("");
        setHasError(false);
        setIsLoading(true);

        handleResponse(
            fetchOperatorImprintAPI(),

            (response) => {
                if (!requestIsActive) {
                    return;
                }

                setImprint(
                    normalizeImprint(
                        response,
                    ),
                );

                setHasError(false);
                setIsLoading(false);
            },

            (message) => {
                if (!requestIsActive) {
                    return;
                }

                setImprint("");
                setHasError(true);
                setIsLoading(false);

                showMessage?.(
                    message
                    ?? MESSAGE.loadError,
                );
            },
        );

        return () => {
            requestIsActive =
                false;
        };
    }, [
        showMessage,
    ]);

    const hasImprint =
        imprint.length > 0;

    return (
        <Paper
            component="main"
            sx={STYLE.root}
        >
            <Box
                sx={{
                    height:
                        CONFIG.topSpacing,
                }}
            />

            {isLoading && (
                <LinearProgress
                    sx={
                        colorStyle.progress
                    }
                />
            )}

            <Box
                component="section"
                sx={{
                    ...STYLE.content,

                    p:
                        CONFIG
                            .contentPadding,

                    pb:
                        CONFIG
                            .bottomSpacing,
                }}
            >
                <Box sx={STYLE.heading}>
                    <Box
                        sx={
                            STYLE
                                .headingTitleGroup
                        }
                    >
                        <DescriptionOutlinedIcon
                            sx={
                                colorStyle
                                    .titleIcon
                            }
                        />

                        <Box
                            sx={
                                STYLE
                                    .headingText
                            }
                        >
                            <Typography
                                variant="h3"
                                component="h1"
                                sx={
                                    colorStyle
                                        .title
                                }
                            >
                                {LABEL.title}
                            </Typography>

                            <Typography
                                variant="body1"
                                component="p"
                                sx={
                                    STYLE
                                        .subtitle
                                }
                            >
                                {LABEL.subtitle}
                            </Typography>
                        </Box>
                    </Box>

                    <Button
                        component={Link}
                        to={ROUTE.home}
                        variant="outlined"
                        startIcon={
                            <HomeOutlinedIcon />
                        }
                        sx={
                            colorStyle
                                .homeButton
                        }
                    >
                        {LABEL.backToHome}
                    </Button>
                </Box>

                <Divider
                    sx={STYLE.divider}
                />

                {!isLoading
                    && hasError
                    && (
                        <Alert
                            severity="error"
                        >
                            {
                                MESSAGE
                                    .loadError
                            }
                        </Alert>
                    )}

                {!isLoading
                    && !hasError
                    && hasImprint
                    && (
                        <Typography
                            component="div"
                            variant="body1"
                            sx={
                                STYLE
                                    .imprintText
                            }
                        >
                            {imprint}
                        </Typography>
                    )}

                {!isLoading
                    && !hasError
                    && !hasImprint
                    && (
                        <Typography
                            component="p"
                            variant="body1"
                            sx={
                                STYLE
                                    .emptyText
                            }
                        >
                            {
                                MESSAGE
                                    .noImprint
                            }
                        </Typography>
                    )}
            </Box>
        </Paper>
    );
}